/**
 * The open balance check (TMMAPP-0132, MM-D-144). Undrawn: a headed card. FIX STARTING BALANCE only
 * when the difference is constant from the start; FIND IT only when a first divergence is served.
 * Adding an adjustment confirms in place, then posts.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import type { BalanceCheck } from '@/api/accounts';
import { HeadedCard } from '@/components/HeadedCard';
import { MoneyText } from '@/components/MoneyText';
import { SubmitButton } from '@/components/SubmitButton';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';

export interface RegisterDivergence {
  transactionId: number | null;
  postedOn: string;
  fileMinor: number;
  ledgerMinor: number;
}

/** The served first divergence, or null when the check doesn't point at a line or date. */
export function divergenceOf(check: BalanceCheck): RegisterDivergence | null {
  if (check.firstDivergenceOn === null) return null;
  if (check.firstDivergenceFileMinor === null || check.firstDivergenceLedgerMinor === null) {
    return null;
  }
  return {
    transactionId: check.firstDivergenceTransactionId,
    postedOn: check.firstDivergenceOn,
    fileMinor: check.firstDivergenceFileMinor,
    ledgerMinor: check.firstDivergenceLedgerMinor,
  };
}

interface BalanceCheckCardProps {
  check: BalanceCheck;
  currency: string | null;
  busy: boolean;
  onFixOpening?: (() => void) | undefined;
  onFind?: ((divergence: RegisterDivergence) => void) | undefined;
  /** Absent for a viewer who cannot post or dismiss. */
  onAdjust?: (() => Promise<void>) | undefined;
  onDismiss?: (() => Promise<void>) | undefined;
}

export function BalanceCheckCard({
  check,
  currency,
  busy,
  onFixOpening,
  onFind,
  onAdjust,
  onDismiss,
}: BalanceCheckCardProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [confirming, setConfirming] = useState(false);
  const [adjustError, setAdjustError] = useState<string | null>(null);
  const adjustRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const divergence = divergenceOf(check);
  const date = formatCivilDate(check.on, locale);

  useEffect(() => {
    if (confirming) cancelRef.current?.focus();
  }, [confirming]);

  function cancelAdjust() {
    setConfirming(false);
    setAdjustError(null);
    requestAnimationFrame(() => adjustRef.current?.focus());
  }

  async function commitAdjust() {
    if (onAdjust === undefined) return;
    setAdjustError(null);
    try {
      await onAdjust();
      setConfirming(false);
    } catch (error) {
      setAdjustError(typeof error === 'string' ? error : t('accounts.errorGeneric'));
    }
  }

  return (
    <HeadedCard title={t('accounts.balanceCheck.title')}>
      <Stack spacing={1.5}>
        <Typography variant="body2">
          <Trans
            i18nKey="accounts.balanceCheck.body"
            values={{ date }}
            components={{
              bank: (
                <MoneyText minor={check.bankBalanceMinor} currency={currency} variant="body2" />
              ),
              ledger: (
                <MoneyText minor={check.ledgerBalanceMinor} currency={currency} variant="body2" />
              ),
              diff: (
                <MoneyText
                  minor={check.differenceMinor}
                  currency={currency}
                  signed
                  variant="body2"
                />
              ),
            }}
          />
        </Typography>
        {confirming ? (
          <Stack spacing={1.5} role="group" aria-labelledby={titleId}>
            <Typography id={titleId} variant="body1" component="p" sx={{ fontWeight: 600 }}>
              <Trans
                i18nKey="accounts.balanceCheck.adjustTitle"
                values={{ date }}
                components={{
                  diff: (
                    <MoneyText
                      minor={check.differenceMinor}
                      currency={currency}
                      signed
                      variant="body1"
                    />
                  ),
                }}
              />
            </Typography>
            <Typography variant="body2">{t('accounts.balanceCheck.adjustBody')}</Typography>
            {adjustError !== null && adjustError.length > 0 && (
              <Alert severity="error">{adjustError}</Alert>
            )}
            <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
              <Button ref={cancelRef} onClick={cancelAdjust} disabled={busy}>
                {t('common.cancel')}
              </Button>
              <SubmitButton loading={busy} onClick={() => void commitAdjust()}>
                {t('accounts.balanceCheck.adjust')}
              </SubmitButton>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {onFixOpening !== undefined && (
              <Button size="small" variant="outlined" onClick={onFixOpening} disabled={busy}>
                {t('accounts.balanceCheck.fixOpening')}
              </Button>
            )}
            {onFind !== undefined && divergence !== null && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onFind(divergence)}
                disabled={busy}
              >
                {t('accounts.balanceCheck.findIt')}
              </Button>
            )}
            {onAdjust !== undefined && (
              <Button
                ref={adjustRef}
                size="small"
                variant="outlined"
                onClick={() => setConfirming(true)}
                disabled={busy}
              >
                {t('accounts.balanceCheck.adjust')}
              </Button>
            )}
            {onDismiss !== undefined && (
              <Button size="small" variant="text" onClick={() => void onDismiss()} disabled={busy}>
                {t('accounts.balanceCheck.dismiss')}
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </HeadedCard>
  );
}
